import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useAuth } from "@/contexts/AuthContext";
import { AuthModal } from "@/components/AuthModal";
import { SubmitQuoteCard } from "@/components/submissions/SubmitQuoteCard";

type AuthMode = "sign-in" | "sign-up";

export function CommunityComingSoonPage() {
  usePageMeta({
    title: "Community, Coming Soon — QuotedsArrow",
    description:
      "A place to discover, save, and vote on quotes shared by fellow readers is on its way. Submit a quote now to help shape it.",
  });

  const { currentUser, isLoading } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>("sign-in");
  const formRef = useRef<HTMLDivElement>(null);
  const isSignedIn = !isLoading && !!currentUser;

  useEffect(() => {
    setAuthOpen(false);
  }, [currentUser]);

  function focusForm() {
    const el = formRef.current;
    if (!el) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    el.focus({ preventScroll: true });
  }

  function openAuth(mode: AuthMode) {
    setAuthMode(mode);
    setAuthOpen(true);
  }

  return (
    <section data-testid="page-community-coming-soon" className="space-y-5">
      <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8">
        <span
          data-testid="status-coming-soon"
          className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-800"
        >
          <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-amber-600" />
          Coming soon
        </span>
        <h1
          data-testid="text-community-title"
          className="mt-4 font-serif text-3xl text-stone-900 sm:text-4xl"
        >
          Community
        </h1>
        <p
          data-testid="text-community-description"
          className="mt-3 max-w-prose text-sm leading-relaxed text-stone-600"
        >
          Discover, save, and vote on quotes shared by fellow readers.
          We&apos;re gathering submissions before opening the Community feed.
        </p>
        <p className="mt-2 max-w-prose text-sm leading-relaxed text-stone-500">
          Help us get started—submit an original quote or a quote you love.
          Every submission goes through review.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          {isLoading ? (
            <div
              data-testid="status-auth-loading"
              aria-hidden="true"
              className="h-10 w-36 animate-pulse rounded-2xl bg-stone-100"
            />
          ) : isSignedIn ? (
            <>
              <button
                type="button"
                data-testid="button-submit-quote-cta"
                onClick={focusForm}
                className="rounded-2xl bg-stone-900 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-stone-800 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-2"
              >
                Submit a quote
              </button>
              <Link
                href="/profile"
                data-testid="link-submission-history"
                className="rounded text-sm font-medium text-stone-600 underline underline-offset-4 transition hover:text-stone-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-2"
              >
                View your submissions
              </Link>
            </>
          ) : (
            <>
              <button
                type="button"
                data-testid="button-submit-quote-cta"
                onClick={() => openAuth("sign-in")}
                className="rounded-2xl bg-stone-900 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-stone-800 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-2"
              >
                Submit a quote
              </button>
              <button
                type="button"
                data-testid="button-sign-in"
                onClick={() => openAuth("sign-in")}
                className="rounded-2xl border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-stone-700 transition hover:bg-stone-50 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-2"
              >
                Sign in
              </button>
              <button
                type="button"
                data-testid="button-create-account"
                onClick={() => openAuth("sign-up")}
                className="rounded-2xl px-4 py-2.5 text-sm font-medium text-stone-600 transition hover:bg-stone-100 hover:text-stone-900 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-2"
              >
                Create account
              </button>
            </>
          )}
        </div>
        {!isLoading && !isSignedIn && (
          <p data-testid="text-guest-note" className="mt-3 text-xs text-stone-400">
            Sign in or create a free account to submit a quote.
          </p>
        )}
      </div>

      {isSignedIn && (
        <div
          id="community-submit"
          ref={formRef}
          tabIndex={-1}
          data-testid="container-community-submit"
          aria-label="Submit a quote"
          className="scroll-mt-6 rounded-3xl focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-300 focus-visible:ring-offset-2"
        >
          <SubmitQuoteCard key={currentUser?.uid} showHistory={false} communityPreview />
        </div>
      )}

      <AuthModal
        isOpen={authOpen}
        initialMode={authMode}
        onClose={() => setAuthOpen(false)}
        customBody="Sign in to submit quotes while Community is being prepared."
      />
    </section>
  );
}
